import { 
    Grid, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    FormGroup, 
    FormControlLabel,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    Tabs,
    Tab
} from "@mui/material";

import { ExpandMore } from "@mui/icons-material";
import { useState, useEffect } from "react";
import MDTypography from "../../../components/MDTypography";
import MDBox from "../../../components/MDBox";
import SmallAssetCard from "../../../components/SmallAssetCard";


function AssetList({assetData, updateIndex, selectedIndex, onFilteredDataChange}) {
    const [checkedItems, setCheckedItems] = useState({
        focusOne: true,
        focusTwo: true,
        focusThree: true,
        focusFour: true,
        focusFive: true,
        focusSix: true,
    });

    const [showAssets, setShowAssets] = useState(true);
    const [showVirtual, setShowVirtual] = useState(true);

    const allChecked = Object.values(checkedItems).every(Boolean);
    const someChecked = Object.values(checkedItems).some(Boolean);

    const handleSelectAll = (event) => {
        const isChecked = event.target.checked;
        setCheckedItems({
            focusOne: isChecked,
            focusTwo: isChecked,
            focusThree: isChecked,
            focusFour: isChecked,
            focusFive: isChecked,
            focusSix: isChecked
        });
    }

    const handleCheckboxChange = (event) => {
        const {name, checked} = event.target;
        setCheckedItems((prev) => ({...prev, [name]: checked}));
    }

    const getFilteredAssets = () => {
        return assetData.filter(asset => {
            // First check if the asset should be shown based on virtual/real toggle
            if (!showVirtual && asset.virtual) return false;
            if (!showAssets && !asset.virtual) return false;

            // Then check focus area
            switch(asset.focus_area) {
                case 1: return checkedItems.focusOne;
                case 2: return checkedItems.focusTwo;
                case 3: return checkedItems.focusThree;
                case 4: return checkedItems.focusFour;
                case 5: return checkedItems.focusFive;
                case 6: return checkedItems.focusSix;
                default: return false;
            }
        });
    };

    const [viewTabIndex, setViewTabIndex] = useState(0);
    const filteredAssets = getFilteredAssets();

    // Update parent with filtered data whenever filters change
    useEffect(() => {
        onFilteredDataChange(filteredAssets);
    }, [filteredAssets, showAssets, showVirtual, checkedItems, onFilteredDataChange]);

    return (
        <Grid container spacing={1} width="100%">
            <Grid item md={12} width="100%">
                <MDBox width="100%" bgColor="navy" p={1} sx={{borderRadius: 2}}>
                    <Paper>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore/>}>
                                <MDTypography variant="h5">Focus Area</MDTypography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                indeterminate={someChecked && !allChecked}
                                                checked={allChecked}
                                                onChange={handleSelectAll}
                                                sx={{
                                                    color: '#2B5587',
                                                    '&.Mui-checked': {
                                                        color: '#2B5587',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Select All"
                                    />
                                    <FormControlLabel 
                                        control={<Checkbox name="focusOne" checked={checkedItems.focusOne} onChange={handleCheckboxChange}/>} 
                                        label="Focus Area 1: Signature Management"/>
                                    <FormControlLabel 
                                        control={<Checkbox name="focusTwo" checked={checkedItems.focusTwo} onChange={handleCheckboxChange}/>} 
                                        label="Focus Area 2: Contested Communications"/>
                                    <FormControlLabel 
                                        control={<Checkbox name="focusThree" checked={checkedItems.focusThree} onChange={handleCheckboxChange}/>} 
                                        label="Focus Area 3: Mobility Applications"/>
                                    <FormControlLabel 
                                        control={<Checkbox name="focusFour" checked={checkedItems.focusFour} onChange={handleCheckboxChange}/>} 
                                        label="Focus Area 4: Non-Traditional Methods"/>
                                    <FormControlLabel 
                                        control={<Checkbox name="focusFive" checked={checkedItems.focusFive} onChange={handleCheckboxChange}/>} 
                                        label="Focus Area 5: Sense and Understand"/>
                                    <FormControlLabel 
                                        control={<Checkbox name="focusSix" checked={checkedItems.focusSix} onChange={handleCheckboxChange}/>} 
                                        label="Focus Area 6: Ubiquitous Access Comms"/>
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>
                    </Paper>                   
                </MDBox>
            </Grid>
            <Grid item md={12}>
                <MDBox width="100%" bgColor="navy" p={1} sx={{borderRadius: 2}}>                    
                    <Paper sx={{p: 2}}>
                        <MDTypography variant="h5">
                            Assets
                        </MDTypography>
                    </Paper>
                    <Paper sx={{p: 1, mt: 1 }}>
                        <FormGroup row>
                            <FormControlLabel 
                                control={<Switch checked={showAssets} onChange={() => setShowAssets(!showAssets)}/>}
                                label="Assets"
                                sx={{pr: 5}}
                            />
                            <FormControlLabel 
                                control={<Switch checked={showVirtual} onChange={() => setShowVirtual(!showVirtual)}/>}
                                label="RCA Virtual Assets"
                            />
                        </FormGroup>

                        <MDBox bgcolor="gray-100">
                            <Tabs
                                value={viewTabIndex}
                                onChange={(_, index) => setViewTabIndex(index)}
                                orientation="horizontal"
                            >
                                <Tab label="Card View" />
                                <Tab label="List View" />
                            </Tabs>
                        </MDBox>

                        {viewTabIndex === 0 && (
                            <MDBox bgColor="grey-300">
                                <Grid container sx={{ 
                                    display: "flex", 
                                    overflowY: "auto", 
                                    maxHeight: "calc(100vh * .64)",
                                    '&::-webkit-scrollbar': {
                                        display: 'none'
                                    },
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none'
                                }}>
                                    {filteredAssets.map((item, index) => (
                                        <Grid item p={1} xs={6}> 
                                            <SmallAssetCard
                                                index={index}
                                                label={item.label} 
                                                title={item.title}
                                                setParentViewIndex={updateIndex}
                                                selectedIndex={selectedIndex}
                                                virtual={item.virtual}   
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </MDBox>
                        )}

                        {viewTabIndex === 1 && (
                            <MDBox bgColor="grey-300">
                                <TableContainer sx={{ 
                                    maxHeight: "calc(100vh * .64)",
                                    '&::-webkit-scrollbar': {
                                        display: 'none'
                                    },
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none'
                                }}>
                                    <Table stickyHeader>
                                        <TableHead sx={{ display: "table-header-group" }}>
                                            <TableRow>
                                                <TableCell sx={{color: "text.main"}}>Asset</TableCell>
                                                <TableCell sx={{color: "text.main"}}>Score</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredAssets.map((item, index) => (
                                                <TableRow 
                                                    key={index} 
                                                    onClick={() => updateIndex(index)}
                                                    sx={{
                                                        '&:hover': {
                                                        transform: "scale(1.05)",
                                                        cursor: 'pointer',
                                                        },
                                                        backgroundColor: selectedIndex === index ? "#D4E2F1" : "#FFFFFF"
                                                    }}
                                                >
                                                    <TableCell>{item.title}</TableCell>
                                                    <TableCell>{item.score}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </MDBox>
                        )}
                    </Paper>
                </MDBox>
            </Grid>
        </Grid>
    );
}

export default AssetList;